import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { getClientProfile } from "@/lib/clientStore";
import useConsultantPWAManifest from "@/lib/useConsultantPWAManifest";
import { Logo, WordMark } from "@/components/Logo";
import PWAInstallBanner from "@/components/PWAInstallBanner";
import { Phone, AlertTriangle, Camera, ArrowRight, Loader2, Shield, Info, ChevronDown, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function ClientLanding() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const submittedCode = searchParams.get("submitted");
  const [consultant, setConsultant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [showAbout, setShowAbout] = useState(false);

  // 🛠 Activate consultant-specific PWA manifest on this page (and all /c/:slug/* subroutes)
  useConsultantPWAManifest(slug, consultant);

  useEffect(() => {
    api.get(`/consultants/by-slug/${slug}`)
      .then(({ data }) => setConsultant(data))
      .catch(() => toast.error("Consulente non trovato"))
      .finally(() => setLoading(false));
    setProfile(getClientProfile());
  }, [slug]);

  // 🚦 Forced first-time registration: if the client has no saved profile yet,
  // send them directly to the onboarding screen so they cannot skip registration.
  useEffect(() => {
    if (!loading && consultant && !profile && consultant.has_active_plan !== false) {
      navigate(`/c/${slug}/onboarding`, { replace: true });
    }
  }, [loading, consultant, profile, slug, navigate]);


  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <Loader2 className="w-10 h-10 animate-spin text-[#2456C6]" />
      </div>
    );
  }

  if (!consultant) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#F8FAFC] p-4">
        <Logo className="h-12 mb-6" />
        <div className="card-gs text-center max-w-md">
          <h1 className="text-2xl font-black text-[#1E293B] mb-2">Consulente non trovato</h1>
          <p className="text-slate-600 mb-4">Il link che hai usato non è valido.</p>
          <Link to="/" className="btn-gs-primary">Torna alla home</Link>
        </div>
      </div>
    );
  }

  const primaryColor = consultant.brand_color_primary || "#F77F2A";
  const startReport = () => {
    if (!profile) navigate(`/c/${slug}/onboarding`);
    else navigate(`/c/${slug}/report`);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* PWA install prompt (mobile only) */}
      <PWAInstallBanner studioName={consultant.studio_name} primaryColor={primaryColor} />

      {/* Quick-mode submission confirmation */}
      {submittedCode && (
        <div className="bg-emerald-50 border-b border-emerald-200 px-4 py-3 flex items-center gap-3" data-testid="submitted-banner">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
          <div className="text-sm">
            <div className="font-black text-emerald-900">Segnalazione inviata ✓</div>
            <div className="text-emerald-700">Codice <strong>{submittedCode}</strong> · Il consulente ti ricontatterà</div>
          </div>
        </div>
      )}

      {/* Branded header — Logo 3:2 prominent, no "Powered by", no description */}
      <header className="bg-[#0F172A] text-white safe-top" data-testid="client-header">
        <div className="max-w-md mx-auto px-5 py-6 flex flex-col items-center text-center">
          {consultant.logo_url ? (
            <div
              className="rounded-2xl bg-white p-3 shadow-lg flex items-center justify-center overflow-hidden"
              style={{ width: 240, height: 160 }}
              data-testid="consultant-logo-container"
            >
              <img
                src={fileUrl(consultant.logo_url)}
                alt={consultant.studio_name}
                className="w-full h-full object-contain"
              />
            </div>
          ) : (
            <div
              className="rounded-2xl flex items-center justify-center font-black text-white shadow-lg"
              style={{ width: 240, height: 160, background: primaryColor, fontSize: 64 }}
              data-testid="consultant-logo-container"
            >
              {consultant.studio_name?.charAt(0) || "C"}
            </div>
          )}
          <div className="font-black text-xl mt-4 truncate max-w-full" data-testid="consultant-studio-name">
            {consultant.studio_name}
          </div>
          {consultant.description && (
            <button
              onClick={() => setShowAbout((v) => !v)}
              data-testid="about-toggle-btn"
              className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-white/70 hover:text-white transition"
            >
              <Info className="w-3.5 h-3.5" />
              {showAbout ? "Chiudi" : "Chi siamo"}
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showAbout ? "rotate-180" : ""}`} />
            </button>
          )}
        </div>
      </header>

      {/* Collapsible "Chi siamo" */}
      {showAbout && consultant.description && (
        <section className="max-w-md mx-auto px-5 pt-4" data-testid="consultant-about-panel">
          <div className="rounded-2xl bg-white border border-slate-200 p-5 shadow-sm text-slate-700 text-[15px] leading-relaxed animate-in fade-in slide-in-from-top-2 duration-200">
            {consultant.description}
          </div>
        </section>
      )}

      {/* Hero */}
      <section className="max-w-md mx-auto px-5 pt-6 pb-6">
        <h1 className="text-3xl sm:text-4xl font-black text-[#1E293B] leading-tight mb-3">
          Hai avuto un<br />
          <span style={{ color: primaryColor }}>incidente?</span>
        </h1>
        <p className="text-slate-600 text-lg leading-relaxed mb-6">
          Apri la pratica con il tuo consulente in meno di un minuto.
        </p>

        <button data-testid="start-report-button" onClick={startReport}
          disabled={!consultant.has_active_plan}
          className="w-full text-white font-black text-lg rounded-2xl py-5 transition-all active:scale-[0.98] flex items-center justify-center gap-3 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ background: primaryColor }}>
          <Camera className="w-6 h-6" strokeWidth={2.5} />
          NUOVA SEGNALAZIONE
          <ArrowRight className="w-5 h-5" />
        </button>

        {consultant.has_active_plan === false && (
          <div className="mt-3 rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900" data-testid="no-plan-message">
            <strong>Servizio temporaneamente non disponibile.</strong> Il consulente non è attivo al momento. Per emergenze utilizza le chiamate rapide qui sotto.
          </div>
        )}

        {profile && consultant.has_active_plan !== false && (
          <Link
            to={`/c/${slug}/profile`}
            data-testid="my-profile-link"
            className="mt-3 inline-flex items-center justify-center gap-2 w-full bg-white border border-slate-200 hover:border-[#2456C6] text-[#1E293B] font-bold py-3 rounded-2xl text-sm transition"
          >
            👤 <span>Ciao <b>{profile.first_name}</b> · Il mio profilo</span>
          </Link>
        )}
      </section>

      {/* Quick calls */}
      <section className="max-w-md mx-auto px-5 pb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold mb-3">Chiamate rapide</div>
        <div className="grid grid-cols-2 gap-3">
          <a href="tel:112" data-testid="call-112-button" className="btn-gs-emergency">
            <AlertTriangle className="w-6 h-6" />
            <span>112<br/><span className="text-xs font-bold">Emergenza</span></span>
          </a>
          {consultant.phone && (
            <a href={`tel:${consultant.phone}`} data-testid="call-consultant-button"
              className="font-black uppercase rounded-xl transition-all active:scale-[0.97] flex flex-col items-center justify-center gap-1 min-h-[80px] text-white shadow-md"
              style={{ background: consultant.brand_color_secondary || "#2456C6" }}>
              <Phone className="w-6 h-6" />
              <span className="text-sm">Consulente</span>
            </a>
          )}
          {(consultant.quick_calls || []).slice(0, 4).map((qc, i) => (
            <a key={i} href={`tel:${qc.number}`} data-testid={`quick-call-${i}-button`}
              className="bg-white border-2 border-slate-200 hover:border-[#1E293B] text-[#1E293B] font-bold rounded-xl transition-all active:scale-[0.97] flex flex-col items-center justify-center gap-1 min-h-[80px]">
              <Phone className="w-5 h-5" strokeWidth={2.5} />
              <span className="text-xs text-center px-2">{qc.label}</span>
            </a>
          ))}
        </div>
      </section>

      {/* Status check (toggleable per-consultant) */}
      {consultant.feature_status_check !== false && (
        <section className="max-w-md mx-auto px-5 pb-6">
          <Link to={`/c/${slug}/practices`} data-testid="check-status-link"
            className="block bg-white border border-slate-200 rounded-2xl p-5 hover:border-[#2456C6] transition-colors">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-bold text-[#1E293B]">Stato pratica</div>
                <div className="text-sm text-slate-500">Controlla con il codice GS-…</div>
              </div>
              <ArrowRight className="w-5 h-5 text-slate-400" />
            </div>
          </Link>
        </section>
      )}

      <footer className="max-w-md mx-auto px-5 py-8 text-center">
        <div className="flex items-center justify-center gap-2 text-xs text-slate-500 mb-1">
          <Shield className="w-3 h-3" /> Dati protetti GDPR · Firma FES eIDAS
        </div>
        <WordMark className="text-sm" />
        <div className="text-[10px] text-slate-400 mt-1">Marchio di TotalKlick srls</div>
      </footer>
    </div>
  );
}
